import {
  dayOfWeek,
  formatMoney,
  getDayOfMonth,
  isPlainDate,
  todayIn,
} from '@paybae/shared'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import { ErrorState, Skeleton } from '@/components/Estado'
import {
  AvisoError,
  Campo,
  FormSheet,
  Guardar,
  Interruptor,
  Monto,
  type OpcionSegmento,
  Segmentos,
  Texto,
} from '@/components/Form'
import type {
  Account,
  Category,
  PaymentSource,
  RecurrenceFrequency,
  RecurringExpense,
  RecurringInput,
} from '@/lib/api'
import { haptic } from '@/lib/haptics'
import {
  useAccounts,
  useActualizarGastoFijo,
  useCards,
  useCategories,
  useCrearGastoFijo,
  useHousehold,
  useRecurring,
} from '@/lib/queries'
import { useSession } from '@/lib/session'
import { mensajeDeError } from '@/routes/Recurring'

/**
 * Alta y edición de un gasto fijo.
 *
 * Lo que se define aquí no es un movimiento sino una **regla**: cada cuánto,
 * cuánto y con qué se paga. De ahí salen dos cosas que el resto de la app da por
 * sentadas — cuánto del periodo ya está comprometido, y el aviso de que algo
 * vence. Por eso la frecuencia y el día no son opcionales: sin ellos no hay
 * calendario que proyectar.
 */

const FRECUENCIAS: ReadonlyArray<OpcionSegmento<RecurrenceFrequency>> = [
  { value: 'MONTHLY', label: 'Mensual', detalle: 'Cada mes' },
  { value: 'SEMI_MONTHLY', label: 'Quincenal', detalle: 'Día 15 y último' },
  { value: 'WEEKLY', label: 'Semanal', detalle: 'Cada 7 días' },
  { value: 'BIWEEKLY', label: 'Catorcenal', detalle: 'Cada 14 días' },
  { value: 'BIMONTHLY', label: 'Bimestral', detalle: 'Cada 2 meses' },
  { value: 'QUARTERLY', label: 'Trimestral', detalle: 'Cada 3 meses' },
  { value: 'YEARLY', label: 'Anual', detalle: 'Una vez al año' },
]

/** 0 es domingo, igual que en el servidor. */
const DIAS_SEMANA: ReadonlyArray<OpcionSegmento<string>> = [
  { value: '0', label: 'Dom' },
  { value: '1', label: 'Lun' },
  { value: '2', label: 'Mar' },
  { value: '3', label: 'Mié' },
  { value: '4', label: 'Jue' },
  { value: '5', label: 'Vie' },
  { value: '6', label: 'Sáb' },
]

const MEDIOS: ReadonlyArray<OpcionSegmento<PaymentSource>> = [
  { value: 'ACCOUNT', label: 'Cuenta o efectivo', detalle: 'Baja el saldo al pagarse' },
  { value: 'CREDIT_CARD', label: 'Tarjeta de crédito', detalle: 'Va al estado de cuenta' },
]

/** Las frecuencias que se anclan a un día del mes. */
const CON_DIA_DEL_MES: ReadonlyArray<RecurrenceFrequency> = [
  'MONTHLY',
  'BIMONTHLY',
  'QUARTERLY',
  'YEARLY',
]

const CON_DIA_DE_LA_SEMANA: ReadonlyArray<RecurrenceFrequency> = ['WEEKLY', 'BIWEEKLY']

/**
 * Orden de las categorías para un gasto fijo: primero las que casi siempre lo
 * son. Los ingresos quedan fuera — un gasto fijo nunca es una categoría de
 * ingreso, y ofrecerla solo invita a clasificar mal.
 */
const RANGO_GRUPO: Record<Category['group'], number> = {
  FIXED: 0,
  DEBT: 1,
  VARIABLE: 2,
  DESPENSA: 3,
  SAVINGS: 4,
  INCOME: 9,
}

export function RecurringForm() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const fijos = useRecurring()

  const cerrar = (): void => {
    void navigate('/fijos')
  }

  if (!id) return <Formulario inicial={null} onCerrar={cerrar} />

  // En edición hay que esperar al registro: sembrar el formulario con valores
  // vacíos y rellenarlos al llegar la respuesta haría que el monto se moviera
  // bajo el dedo de quien ya empezó a corregirlo.
  if (fijos.isPending) {
    return (
      <Hoja titulo="Editar gasto fijo" onCerrar={cerrar}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex flex-col gap-1.5">
            <Skeleton className="h-[15px] w-32" />
            <Skeleton className="h-12" />
          </div>
        ))}
      </Hoja>
    )
  }

  const actual = (fijos.data ?? []).find((f) => f.id === id) ?? null

  if (fijos.isError || !actual) {
    return (
      <Hoja titulo="Editar gasto fijo" onCerrar={cerrar}>
        <ErrorState
          titulo="No encontramos ese gasto fijo"
          mensaje={
            fijos.error instanceof Error
              ? fijos.error.message
              : 'Puede que se haya eliminado desde otro dispositivo.'
          }
          onReintentar={() => void fijos.refetch()}
        />
      </Hoja>
    )
  }

  // `key` fuerza un formulario nuevo al cambiar de registro: así el estado
  // inicial sale del prop y no hace falta un efecto que lo sincronice.
  return <Formulario key={actual.id} inicial={actual} onCerrar={cerrar} />
}

/** Hoja sin acción utilizable, para los estados de carga y error. */
function Hoja({
  titulo,
  onCerrar,
  children,
}: {
  titulo: string
  onCerrar: () => void
  children: React.ReactNode
}) {
  return (
    <FormSheet tinte="peach" titulo={titulo} onCerrar={onCerrar} accion={<Guardar onClick={onCerrar} disabled />}>
      {children}
    </FormSheet>
  )
}

function Formulario({
  inicial,
  onCerrar,
}: {
  inicial: RecurringExpense | null
  onCerrar: () => void
}) {
  const { membership } = useSession()
  // La fecha sale del huso del hogar, no del reloj del navegador: a las 23:30 en
  // México un teléfono en UTC ya estaría en el día siguiente.
  const hoy = todayIn(membership.household.timezone)
  // Un rol limitado no puede tocar el fondo común: el servidor responde 403, así
  // que la opción no se dibuja en vez de ofrecer algo que falla al guardar.
  const veElHogar = membership.role === 'ADMIN' || membership.role === 'MEMBER'

  const categorias = useCategories()
  const cuentas = useAccounts()
  const tarjetas = useCards()
  const hogar = useHousehold()

  const crear = useCrearGastoFijo()
  const actualizar = useActualizarGastoFijo()

  const [nombre, setNombre] = useState(inicial?.name ?? '')
  const [categoriaId, setCategoriaId] = useState(inicial?.categoryId ?? '')
  const [montoCents, setMontoCents] = useState(montoInicial(inicial))
  const [variable, setVariable] = useState(inicial?.isVariableAmount ?? false)
  const [frecuencia, setFrecuencia] = useState<RecurrenceFrequency>(inicial?.frequency ?? 'MONTHLY')
  const [diaMes, setDiaMes] = useState(
    String(inicial?.dayOfMonth ?? getDayOfMonth(hoy)),
  )
  const [diaSemana, setDiaSemana] = useState(String(inicial?.dayOfWeek ?? dayOfWeek(hoy)))
  const [inicio, setInicio] = useState<string>(inicial?.startsOn ?? hoy)
  const [fuente, setFuente] = useState<PaymentSource>(inicial?.paymentSource ?? 'ACCOUNT')
  const [cuentaId, setCuentaId] = useState(inicial?.accountId ?? '')
  const [tarjetaId, setTarjetaId] = useState(inicial?.creditCardId ?? '')
  const [atribuido, setAtribuido] = useState(
    inicial?.attributedToMemberId ?? (veElHogar ? '' : membership.id),
  )
  const [autoPost, setAutoPost] = useState(inicial?.autoPost ?? false)
  const [activo, setActivo] = useState(inicial?.active ?? true)
  const [tocado, setTocado] = useState(false)

  const cargando =
    categorias.isPending || cuentas.isPending || tarjetas.isPending || hogar.isPending

  const categoriaElegida = (categorias.data ?? []).find((c) => c.id === categoriaId) ?? null

  /**
   * Los vales de despensa solo pagan categorías de despensa: es una regla del
   * emisor, y el servidor rechaza lo demás. Se filtran antes de dibujarlos —una
   * opción que no se puede usar no debería llegar a la pantalla.
   */
  const cuentasElegibles = (cuentas.data ?? []).filter(
    (c) => !c.archivedAt && (c.type !== 'MEAL_VOUCHER' || categoriaElegida?.isDespensaEligible),
  )
  const tarjetasElegibles = (tarjetas.data ?? []).filter((t) => !t.archivedAt)
  const integrantes = hogar.data?.members ?? []

  const pideDiaMes = CON_DIA_DEL_MES.includes(frecuencia)
  const pideDiaSemana = CON_DIA_DE_LA_SEMANA.includes(frecuencia)
  const diaMesNumero = diaMes.trim() === '' ? Number.NaN : Number(diaMes)

  const errores = {
    nombre:
      nombre.trim().length >= 2 ? undefined : 'Ponle un nombre: así lo reconocerás en la lista.',
    monto: montoCents > 0 ? undefined : 'El monto debe ser mayor a cero.',
    diaMes:
      !pideDiaMes || (Number.isInteger(diaMesNumero) && diaMesNumero >= 0 && diaMesNumero <= 31)
        ? undefined
        : 'Del 1 al 31, o 0 para el último día del mes.',
    inicio: isPlainDate(inicio) ? undefined : 'Elige desde cuándo corre este gasto.',
    medio:
      fuente === 'ACCOUNT'
        ? cuentaId
          ? undefined
          : 'Elige la cuenta con la que se paga.'
        : tarjetaId
          ? undefined
          : 'Elige la tarjeta con la que se paga.',
  }
  const completo = Object.values(errores).every((e) => e === undefined)

  const elegirCategoria = (next: string): void => {
    setCategoriaId(next)
    // Si estaba elegida una cuenta de vales y la categoría nueva no los admite,
    // se limpia: dejarla puesta guardaría un gasto fijo que el servidor rechaza.
    const cuenta = (cuentas.data ?? []).find((c) => c.id === cuentaId)
    const nueva = (categorias.data ?? []).find((c) => c.id === next)
    if (cuenta?.type === 'MEAL_VOUCHER' && !nueva?.isDespensaEligible) setCuentaId('')
  }

  const guardando = crear.isPending || actualizar.isPending
  const errorGuardado = crear.error ?? actualizar.error
  const fallo = crear.isError || actualizar.isError

  const guardar = (): void => {
    setTocado(true)
    if (!completo || guardando) return
    // Repetida aquí dentro y no heredada de `errores.inicio`: es la comprobación
    // que convierte la cadena del input en `PlainDate`.
    if (!isPlainDate(inicio)) return

    const datos: RecurringInput = {
      name: nombre.trim(),
      amountCents: montoCents,
      isVariableAmount: variable,
      // Un gasto variable exige estimado en el servidor, y la pantalla captura
      // un solo importe: se manda en ambos campos. El monto real del recibo
      // llega después, al marcarlo pagado desde la lista.
      estimateCents: variable ? montoCents : null,
      frequency: frecuencia,
      // La quincenal no lleva día: el servidor la fija en el 15 y el último.
      dayOfMonth: pideDiaMes ? diaMesNumero : null,
      dayOfWeek: pideDiaSemana ? Number(diaSemana) : null,
      startsOn: inicio,
      paymentSource: fuente,
      accountId: fuente === 'ACCOUNT' ? cuentaId : null,
      creditCardId: fuente === 'CREDIT_CARD' ? tarjetaId : null,
      categoryId: categoriaId || null,
      // Cadena vacía significa fondo común, que el servidor espera como nulo.
      attributedToMemberId: atribuido || null,
      autoPost,
    }

    const opciones = {
      onSuccess: () => {
        haptic('success')
        onCerrar()
      },
      // Sin `reset` ni limpieza: conservar lo capturado es lo que hace que el
      // segundo intento tras un fallo de red no cueste ni un toque más.
      onError: () => haptic('error'),
    }

    if (inicial) actualizar.mutate({ id: inicial.id, ...datos, active: activo }, opciones)
    else crear.mutate(datos, opciones)
  }

  if (cargando) {
    return (
      <Hoja titulo={inicial ? 'Editar gasto fijo' : 'Nuevo gasto fijo'} onCerrar={onCerrar}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex flex-col gap-1.5">
            <Skeleton className="h-[15px] w-32" />
            <Skeleton className="h-12" />
          </div>
        ))}
      </Hoja>
    )
  }

  return (
    <FormSheet
      tinte="peach"
      titulo={inicial ? 'Editar gasto fijo' : 'Nuevo gasto fijo'}
      onCerrar={onCerrar}
      accion={
        <div className="flex flex-col gap-2.5">
          {/* El error del servidor se queda a la vista con el formulario
              intacto: es lo único que permite corregir el campo que señala. */}
          {fallo ? <AvisoError mensaje={mensajeDeError(errorGuardado)} /> : null}
          <Guardar onClick={guardar} disabled={!completo} guardando={guardando}>
            {completo ? (inicial ? 'Guardar cambios' : 'Crear gasto fijo') : queFalta(errores)}
          </Guardar>
        </div>
      }
    >
      <Campo label="Nombre" error={tocado ? errores.nombre : undefined}>
        <Texto
          value={nombre}
          onChange={setNombre}
          placeholder="Renta, luz, colegiatura…"
          maxLength={80}
          invalido={tocado && errores.nombre !== undefined}
        />
      </Campo>

      {/* El monto va antes que la categoría, aunque la categoría sea el campo
          «más chico»: la lista de categorías ocupa media pantalla y dejaría el
          único dato que nadie puede adivinar escondido bajo el scroll. */}
      <Campo
        label={variable ? 'Estimado del recibo' : 'Monto'}
        ayuda={
          variable
            ? 'Con esto lo proyectamos. Al marcarlo pagado escribes lo que llegó de verdad.'
            : undefined
        }
        error={tocado ? errores.monto : undefined}
      >
        <Monto cents={montoCents} onChange={setMontoCents} />
      </Campo>

      <Interruptor
        label="El monto cambia cada periodo"
        ayuda="Para la luz, el agua o el gas: apartamos el estimado y lo ajustamos con el recibo real."
        value={variable}
        onChange={setVariable}
      />

      <Campo
        label="Categoría"
        ayuda="Define en qué grupo suma este gasto dentro de tus gráficas."
      >
        <Segmentos
          opciones={opcionesCategoria(categorias.data ?? [])}
          value={categoriaId}
          onChange={elegirCategoria}
        />
      </Campo>

      <Campo label="Cada cuánto se paga">
        <Segmentos opciones={FRECUENCIAS} value={frecuencia} onChange={setFrecuencia} />
      </Campo>

      {pideDiaMes ? (
        <Campo
          label="Día del mes"
          ayuda="Del 1 al 31. Escribe 0 para el último día: en febrero cae el 28 o el 29 sin que tengas que cambiarlo."
          error={tocado ? errores.diaMes : undefined}
        >
          <Texto
            value={diaMes}
            onChange={setDiaMes}
            inputMode="numeric"
            maxLength={2}
            placeholder="1"
            invalido={tocado && errores.diaMes !== undefined}
          />
        </Campo>
      ) : null}

      {pideDiaSemana ? (
        <Campo label="Día de la semana">
          <Segmentos
            opciones={DIAS_SEMANA}
            value={diaSemana}
            onChange={setDiaSemana}
            columnas={3}
          />
        </Campo>
      ) : null}

      {frecuencia === 'SEMI_MONTHLY' ? (
        <p className="rounded-card bg-surface-sunken px-3 py-2.5 text-[12px] leading-snug text-ink-soft">
          Quincenal en México son dos fechas fijas —el 15 y el último día del mes—, no cada 14
          días. Por eso no lleva día configurable.
        </p>
      ) : null}

      <Campo
        label="Desde cuándo"
        ayuda="Si pones una fecha pasada, arrancamos desde hoy: dar de alta un gasto que lleva un año corriendo no debe generar doce cargos de golpe."
        error={tocado ? errores.inicio : undefined}
      >
        {/* Fecha como texto `AAAA-MM-DD`, que es lo que entrega el input nativo
            y lo que espera el servidor. Nunca un `Date`. */}
        <input
          type="date"
          value={inicio}
          onChange={(e) => setInicio(e.target.value)}
          className="min-h-[48px] w-full rounded-card border border-line bg-surface px-3.5 text-[16px] text-ink"
        />
      </Campo>

      <Campo label="¿Con qué se paga?">
        <Segmentos opciones={MEDIOS} value={fuente} onChange={setFuente} />
      </Campo>

      {fuente === 'ACCOUNT' ? (
        <Campo
          label="Cuenta"
          ayuda="El día que se registre, el saldo baja de aquí."
          error={tocado ? errores.medio : undefined}
        >
          {cuentasElegibles.length === 0 ? (
            <p className="rounded-card bg-surface-sunken px-3 py-2.5 text-[12px] leading-snug text-ink-soft">
              Todavía no tienes cuentas. Agrega una desde Ajustes y vuelve aquí.
            </p>
          ) : (
            <Segmentos
              opciones={opcionesCuenta(cuentasElegibles)}
              value={cuentaId}
              onChange={setCuentaId}
            />
          )}
        </Campo>
      ) : (
        <Campo
          label="Tarjeta"
          ayuda="No sale de tu efectivo el día que vence: se suma al estado de cuenta y se paga cuando ese corte venza."
          error={tocado ? errores.medio : undefined}
        >
          {tarjetasElegibles.length === 0 ? (
            <p className="rounded-card bg-surface-sunken px-3 py-2.5 text-[12px] leading-snug text-ink-soft">
              Todavía no tienes tarjetas. Agrega una desde Ajustes y vuelve aquí.
            </p>
          ) : (
            <Segmentos
              opciones={tarjetasElegibles.map((t) => ({
                value: t.id,
                label: `${t.name}${t.last4 ? ` ·· ${t.last4}` : ''}`,
                detalle: `Disponible ${formatMoney(t.availableCreditCents, { hideZeroCents: true })}`,
              }))}
              value={tarjetaId}
              onChange={setTarjetaId}
            />
          )}
        </Campo>
      )}

      <Campo
        label="¿De quién es?"
        ayuda="Lo del fondo común se reparte entre el hogar; lo personal se descuenta solo de quien lo paga."
      >
        <Segmentos
          opciones={opcionesIntegrante(integrantes, veElHogar ? null : membership.id)}
          value={atribuido}
          onChange={setAtribuido}
        />
      </Campo>

      <Interruptor
        label="Registrar solo"
        ayuda="El cargo se crea automáticamente el día que vence, sin que tengas que abrir la app. Si lo dejas apagado, te aparece en la lista para que lo marques tú."
        value={autoPost}
        onChange={setAutoPost}
      />

      {inicial ? (
        <Interruptor
          label="Activo"
          ayuda="Apágalo si dejaste de pagarlo. No se borra: los movimientos que ya generó lo siguen apuntando y tus gráficas del año siguen cuadrando."
          value={activo}
          onChange={setActivo}
        />
      ) : null}
    </FormSheet>
  )
}

/**
 * Con qué importe abrir el formulario.
 *
 * En un gasto variable lo que se edita es el estimado: es el que proyecta el
 * periodo. `amountCents` queda como respaldo del último importe conocido.
 */
function montoInicial(inicial: RecurringExpense | null): number {
  if (!inicial) return 0
  if (inicial.isVariableAmount) return inicial.estimateCents ?? inicial.amountCents
  return inicial.amountCents
}

function opcionesCategoria(categorias: readonly Category[]): ReadonlyArray<OpcionSegmento<string>> {
  const vivas = categorias
    .filter((c) => !c.archivedAt && c.group !== 'INCOME')
    // `sort` es estable: dentro de cada grupo se conserva el orden del servidor.
    .sort((a, b) => RANGO_GRUPO[a.group] - RANGO_GRUPO[b.group])
  return [
    { value: '', label: 'Sin categoría', detalle: 'La defines después' },
    ...vivas.map((c) => ({ value: c.id, label: c.name })),
  ]
}

function opcionesCuenta(cuentas: readonly Account[]): ReadonlyArray<OpcionSegmento<string>> {
  return cuentas.map((c) => ({
    value: c.id,
    label: c.name,
    detalle: `Saldo ${formatMoney(c.balanceCents, { hideZeroCents: true })}`,
  }))
}

/**
 * A quién se puede atribuir el gasto.
 *
 * Un rol limitado solo puede crear lo suyo: el fondo común y lo ajeno los
 * rechaza el servidor con un 403. `soloMiembroId` deja únicamente su opción, en
 * vez de ofrecer un selector cuyas demás casillas fallan al guardar.
 */
function opcionesIntegrante(
  integrantes: ReadonlyArray<{ id: string; user: { displayName: string } }>,
  soloMiembroId: string | null,
): ReadonlyArray<OpcionSegmento<string>> {
  const visibles =
    soloMiembroId === null ? integrantes : integrantes.filter((m) => m.id === soloMiembroId)
  const personas = visibles.map((m) => ({
    value: m.id,
    label: m.user.displayName.split(' ')[0] ?? m.user.displayName,
    detalle: 'Gasto personal',
  }))
  if (soloMiembroId !== null) return personas
  return [{ value: '', label: 'Común', detalle: 'Fondo del hogar' }, ...personas]
}

/** Dice qué falta, en vez de un botón muerto sin explicación. */
function queFalta(errores: Record<string, string | undefined>): string {
  if (errores.nombre) return 'Ponle un nombre'
  if (errores.monto) return 'Escribe el monto'
  if (errores.diaMes) return 'Revisa el día del mes'
  if (errores.inicio) return 'Elige desde cuándo'
  if (errores.medio) return 'Elige con qué se paga'
  return 'Guardar'
}
